import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCoaching } from '../../contexts/CoachingContext';
import axios from 'axios';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import toast from 'react-hot-toast';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ── Axes & Questions ─────────────────────────────────────────────────────────

const DIAGNOSTIC_AXES = [
  {
    key: 'marche',
    label: 'Marché',
    description: 'Analyse du marché, de la concurrence, de la clientèle et de la stratégie commerciale',
    colorClass: 'bg-blue-50 text-blue-700 border-blue-200',
    dotColor: '#3b82f6',
    questions: [
      { id: 'q1', text: 'La taille et l\'attractivité du marché cible sont-elles bien évaluées ?' },
      { id: 'q2', text: 'La clientèle cible est-elle clairement identifiée et segmentée ?' },
      { id: 'q3', text: 'La concurrence directe et indirecte est-elle analysée en profondeur ?' },
      { id: 'q4', text: 'Les canaux de distribution et de vente sont-ils efficaces et diversifiés ?' },
      { id: 'q5', text: 'La stratégie marketing et commerciale est-elle adaptée et cohérente ?' },
      { id: 'q6', text: 'Des marchés ou segments non encore exploités ont-ils été identifiés ?' },
      { id: 'q7', text: 'L\'entreprise dispose-t-elle d\'un avantage concurrentiel distinctif et durable ?' },
      { id: 'q8', text: 'La notoriété et l\'image de marque sont-elles bien établies auprès des clients ?' },
      { id: 'q9', text: 'L\'évolution des tendances du marché est-elle suivie et intégrée dans la stratégie ?' },
    ]
  },
  {
    key: 'appro',
    label: 'Approvisionnement & Production',
    description: 'Chaîne d\'approvisionnement, processus de production, qualité et coûts opérationnels',
    colorClass: 'bg-orange-50 text-orange-700 border-orange-200',
    dotColor: '#f97316',
    questions: [
      { id: 'q1', text: 'La qualité et la fiabilité des fournisseurs sont-elles satisfaisantes ?' },
      { id: 'q2', text: 'Les délais et coûts d\'approvisionnement sont-ils maîtrisés ?' },
      { id: 'q3', text: 'Le processus de production est-il optimisé, documenté et efficient ?' },
      { id: 'q4', text: 'Le contrôle qualité des produits/services est-il systématique et rigoureux ?' },
      { id: 'q5', text: 'Les coûts de production sont-ils compétitifs par rapport au marché ?' },
      { id: 'q6', text: 'Les équipements et outils de production sont-ils adaptés et en bon état ?' },
      { id: 'q7', text: 'Les risques de rupture d\'approvisionnement sont-ils identifiés et gérés ?' },
      { id: 'q8', text: 'La capacité de production est-elle adaptée à la demande actuelle et future ?' },
      { id: 'q9', text: 'Des stocks de sécurité et une gestion des stocks efficace sont-ils en place ?' },
    ]
  },
  {
    key: 'rh',
    label: 'Ressources Humaines',
    description: 'Compétences, recrutement, formation, climat social et organisation des équipes',
    colorClass: 'bg-green-50 text-green-700 border-green-200',
    dotColor: '#22c55e',
    questions: [
      { id: 'q1', text: 'Les compétences clés nécessaires à l\'activité sont-elles présentes en interne ?' },
      { id: 'q2', text: 'La politique de recrutement est-elle efficace pour attirer les bons profils ?' },
      { id: 'q3', text: 'Un plan de formation et de développement des compétences est-il en place ?' },
      { id: 'q4', text: 'Le climat social et la motivation des équipes sont-ils satisfaisants ?' },
      { id: 'q5', text: 'La politique de rémunération est-elle compétitive et équitable ?' },
      { id: 'q6', text: 'Un organigramme clair et des fiches de poste sont-ils formalisés ?' },
      { id: 'q7', text: 'La rétention des talents clés est-elle assurée efficacement ?' },
      { id: 'q8', text: 'Le dirigeant dispose-t-il des compétences managériales et entrepreneuriales requises ?' },
      { id: 'q9', text: 'La succession et la continuité du leadership sont-elles planifiées ?' },
    ]
  },
  {
    key: 'finance',
    label: 'Finance',
    description: 'Structure financière, trésorerie, financement, endettement et accès aux capitaux',
    colorClass: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    dotColor: '#eab308',
    questions: [
      { id: 'q1', text: 'La structure financière (fonds propres vs dettes) est-elle saine et équilibrée ?' },
      { id: 'q2', text: 'La trésorerie est-elle suffisante pour couvrir les besoins à court terme ?' },
      { id: 'q3', text: 'Le financement des investissements futurs est-il planifié et sécurisé ?' },
      { id: 'q4', text: 'Le niveau d\'endettement est-il raisonnable et soutenable ?' },
      { id: 'q5', text: 'L\'accès au crédit bancaire ou aux financements alternatifs est-il facilité ?' },
      { id: 'q6', text: 'Les coûts financiers (intérêts, charges bancaires) sont-ils maîtrisés ?' },
      { id: 'q7', text: 'Le besoin en fonds de roulement (BFR) est-il correctement anticipé et géré ?' },
      { id: 'q8', text: 'Des garanties, cautions ou fonds de garantie sont-ils mobilisables ?' },
      { id: 'q9', text: 'La gestion des créances clients et des délais de paiement est-elle optimisée ?' },
    ]
  },
  {
    key: 'gestion',
    label: 'Gestion & Comptabilité',
    description: 'Tenue comptable, outils de gestion, budget, tableaux de bord et conformité fiscale',
    colorClass: 'bg-purple-50 text-purple-700 border-purple-200',
    dotColor: '#a855f7',
    questions: [
      { id: 'q1', text: 'La comptabilité est-elle tenue régulièrement, à jour et par un professionnel ?' },
      { id: 'q2', text: 'Les états financiers (bilan, compte de résultat) sont-ils produits dans les délais ?' },
      { id: 'q3', text: 'Un budget prévisionnel annuel est-il élaboré, suivi et mis à jour ?' },
      { id: 'q4', text: 'Des outils et logiciels de gestion adaptés à l\'activité sont-ils utilisés ?' },
      { id: 'q5', text: 'Les obligations fiscales, sociales et légales sont-elles respectées ?' },
      { id: 'q6', text: 'Des tableaux de bord de suivi de l\'activité sont-ils utilisés régulièrement ?' },
      { id: 'q7', text: 'Le contrôle interne est-il organisé pour prévenir les erreurs et fraudes ?' },
      { id: 'q8', text: 'Les flux de trésorerie sont-ils analysés, planifiés et anticipés ?' },
      { id: 'q9', text: 'Une analyse des écarts entre budget prévu et réalisé est-elle effectuée ?' },
    ]
  },
  {
    key: 'gouvernance',
    label: 'Gouvernance',
    description: 'Structure juridique, prise de décision, conformité, gestion des risques et vision stratégique',
    colorClass: 'bg-red-50 text-red-700 border-red-200',
    dotColor: '#ef4444',
    questions: [
      { id: 'q1', text: 'La structure juridique de l\'entreprise est-elle adaptée à son activité et sa taille ?' },
      { id: 'q2', text: 'Les rôles et responsabilités des dirigeants et associés sont-ils clairement définis ?' },
      { id: 'q3', text: 'Un conseil d\'administration, comité de direction ou conseil consultatif existe-t-il ?' },
      { id: 'q4', text: 'Les processus de prise de décision sont-ils transparents, rapides et efficaces ?' },
      { id: 'q5', text: 'La conformité réglementaire, légale et sectorielle est-elle assurée en continu ?' },
      { id: 'q6', text: 'Les risques opérationnels, financiers et stratégiques sont-ils identifiés et gérés ?' },
      { id: 'q7', text: 'La communication interne entre les dirigeants et équipes est-elle efficace ?' },
      { id: 'q8', text: 'Une vision, une mission et une stratégie à moyen/long terme sont-elles formalisées ?' },
      { id: 'q9', text: 'La protection des actifs (propriété, contrats, assurances) est-elle assurée ?' },
    ]
  },
  {
    key: 'rentabilite',
    label: 'Rentabilité',
    description: 'Chiffre d\'affaires, marges, seuil de rentabilité, politique de prix et création de valeur',
    colorClass: 'bg-teal-50 text-teal-700 border-teal-200',
    dotColor: '#14b8a6',
    questions: [
      { id: 'q1', text: 'Le chiffre d\'affaires est-il en croissance régulière et conforme aux prévisions ?' },
      { id: 'q2', text: 'Les marges brutes et nettes sont-elles satisfaisantes et en amélioration ?' },
      { id: 'q3', text: 'Le seuil de rentabilité (point mort) est-il atteint et largement dépassé ?' },
      { id: 'q4', text: 'Les coûts fixes et variables sont-ils bien identifiés et optimisés ?' },
      { id: 'q5', text: 'La politique de prix est-elle cohérente, rentable et en phase avec le marché ?' },
      { id: 'q6', text: 'Les sources et lignes de revenus sont-elles suffisamment diversifiées ?' },
      { id: 'q7', text: 'Le retour sur investissement (ROI) des actions et projets est-il mesuré ?' },
      { id: 'q8', text: 'La valeur créée pour les associés/actionnaires est-elle satisfaisante ?' },
      { id: 'q9', text: 'L\'entreprise dispose-t-elle d\'une capacité d\'autofinancement suffisante ?' },
    ]
  },
  {
    key: 'rd',
    label: 'Recherche & Développement',
    description: 'Innovation, veille technologique, propriété intellectuelle et transformation numérique',
    colorClass: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dotColor: '#6366f1',
    questions: [
      { id: 'q1', text: 'Un budget dédié à la R&D et à l\'innovation est-il alloué et suivi ?' },
      { id: 'q2', text: 'L\'innovation produit, service ou procédé est-elle continue et structurée ?' },
      { id: 'q3', text: 'Une veille technologique, sectorielle et concurrentielle est-elle assurée ?' },
      { id: 'q4', text: 'Des partenariats de recherche, avec universités ou institutions, existent-ils ?' },
      { id: 'q5', text: 'La propriété intellectuelle (brevets, marques, logiciels) est-elle protégée ?' },
      { id: 'q6', text: 'La transformation numérique est-elle intégrée dans la stratégie de l\'entreprise ?' },
      { id: 'q7', text: 'La capacité d\'adaptation rapide aux évolutions technologiques est-elle forte ?' },
      { id: 'q8', text: 'L\'entreprise bénéficie-t-elle d\'incitations fiscales ou de subventions pour la R&D ?' },
      { id: 'q9', text: 'Des prototypes, tests ou pilotes sont-ils réalisés avant le lancement de nouveautés ?' },
    ]
  },
  {
    key: 'durable',
    label: 'Développement Durable',
    description: 'Impact environnemental, RSE, pratiques éco-responsables et engagement sociétal',
    colorClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dotColor: '#10b981',
    questions: [
      { id: 'q1', text: 'L\'impact environnemental de l\'activité est-il mesuré et documenté ?' },
      { id: 'q2', text: 'Des pratiques éco-responsables (énergie, déchets, eau) sont-elles intégrées ?' },
      { id: 'q3', text: 'Une politique RSE (Responsabilité Sociale des Entreprises) est-elle formalisée ?' },
      { id: 'q4', text: 'Les fournisseurs sont-ils évalués et sélectionnés selon des critères de durabilité ?' },
      { id: 'q5', text: 'L\'entreprise intègre-t-elle des enjeux sociaux : inclusion, diversité, emploi local ?' },
      { id: 'q6', text: 'La conformité aux réglementations environnementales est-elle assurée et documentée ?' },
      { id: 'q7', text: 'L\'empreinte carbone de l\'entreprise est-elle évaluée et réduite progressivement ?' },
      { id: 'q8', text: 'L\'entreprise communique-t-elle sur ses engagements et actions durables ?' },
      { id: 'q9', text: 'Des objectifs de développement durable sont-ils intégrés dans la stratégie globale ?' },
    ]
  },
];

const SWOT_CATEGORIES = {
  force:      { label: 'Force',       bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300',  icon: '✦' },
  faiblesse:  { label: 'Faiblesse',   bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300',    icon: '▼' },
  opportunite:{ label: 'Opportunité', bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   icon: '◆' },
  menace:     { label: 'Menace',      bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: '⚠' },
};

const autoCategory = (score) => {
  if (score >= 4) return 'force';
  if (score <= 2) return 'faiblesse';
  return null;
};

const scoreBg = (score) => {
  if (!score) return 'bg-gray-100 text-gray-400';
  if (score >= 4) return 'bg-green-100 text-green-700';
  if (score === 3) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconChevron = ({ open }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const IconSave = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);

const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const IconTrash = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// ── Sub-components ────────────────────────────────────────────────────────────

const StarRating = ({ value, onChange }) => (
  <div className="flex space-x-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? 0 : n)}
        className={`w-7 h-7 rounded text-sm font-bold transition-all ${
          n <= value
            ? n >= 4 ? 'bg-green-500 text-white' : n === 3 ? 'bg-yellow-400 text-white' : 'bg-red-400 text-white'
            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
        }`}
        title={['', 'Très insuffisant', 'Insuffisant', 'Moyen', 'Satisfaisant', 'Excellent'][n]}
      >
        {n}
      </button>
    ))}
  </div>
);

const RadarChart = ({ responses }) => {
  const scores = DIAGNOSTIC_AXES.map(axis => {
    const axisData = responses[axis.key] || {};
    const scored = axis.questions.map(q => axisData[q.id]?.score || 0).filter(s => s > 0);
    return scored.length > 0 ? scored.reduce((a, b) => a + b, 0) / scored.length : 0;
  });

  const data = {
    labels: DIAGNOSTIC_AXES.map(a => a.label),
    datasets: [{
      label: 'Score moyen',
      data: scores,
      backgroundColor: 'rgba(99,102,241,0.15)',
      borderColor: 'rgba(99,102,241,0.8)',
      borderWidth: 2,
      pointBackgroundColor: DIAGNOSTIC_AXES.map(a => a.dotColor),
      pointRadius: 5,
      pointHoverRadius: 7,
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      r: {
        min: 0,
        max: 5,
        ticks: { stepSize: 1, font: { size: 10 } },
        grid: { color: 'rgba(0,0,0,0.08)' },
        pointLabels: { font: { size: 11 } },
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => `Score : ${ctx.raw.toFixed(1)} / 5` } }
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <Radar data={data} options={options} />
    </div>
  );
};

const SwotMatrix = ({ responses }) => {
  const items = { force: [], faiblesse: [], opportunite: [], menace: [] };

  DIAGNOSTIC_AXES.forEach(axis => {
    const axisData = responses[axis.key] || {};
    axis.questions.forEach(q => {
      const entry = axisData[q.id];
      if (!entry || !entry.score) return;
      const cat = entry.category || autoCategory(entry.score);
      if (cat && items[cat]) {
        items[cat].push({ axis: axis.label, question: q.text, score: entry.score, comment: entry.comment || '' });
      }
    });
  });

  const quadrants = [
    { key: 'force',       title: 'Forces',       subtitle: 'Atouts internes à exploiter',    bgHeader: 'bg-green-600',  bgBody: 'bg-green-50'  },
    { key: 'faiblesse',   title: 'Faiblesses',   subtitle: 'Points internes à améliorer',    bgHeader: 'bg-red-600',    bgBody: 'bg-red-50'    },
    { key: 'opportunite', title: 'Opportunités', subtitle: 'Facteurs externes favorables',   bgHeader: 'bg-blue-600',   bgBody: 'bg-blue-50'   },
    { key: 'menace',      title: 'Menaces',      subtitle: 'Facteurs externes à surveiller', bgHeader: 'bg-orange-600', bgBody: 'bg-orange-50' },
  ];

  const total = Object.values(items).reduce((s, a) => s + a.length, 0);
  if (total === 0) {
    return <div className="text-center py-8 text-gray-400 text-sm">Complétez le diagnostic pour voir la matrice SWOT.</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {quadrants.map(q => (
        <div key={q.key} className="rounded-lg overflow-hidden border border-gray-200">
          <div className={`${q.bgHeader} text-white px-4 py-2`}>
            <div className="font-semibold">{q.title}</div>
            <div className="text-xs opacity-80">{q.subtitle}</div>
          </div>
          <div className={`${q.bgBody} p-3 min-h-[120px]`}>
            {items[q.key].length === 0 ? (
              <p className="text-xs text-gray-400 italic">Aucun élément classé ici</p>
            ) : (
              <ul className="space-y-2">
                {items[q.key].map((item, i) => (
                  <li key={i} className="text-xs">
                    <div className="flex items-start space-x-2">
                      <span className={`shrink-0 font-bold w-5 h-5 rounded-full flex items-center justify-center text-white text-xs ${
                        item.score >= 4 ? 'bg-green-500' : item.score === 3 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}>{item.score}</span>
                      <div>
                        <span className="font-medium text-gray-700">[{item.axis}]</span>{' '}
                        <span className="text-gray-600">{item.question}</span>
                        {item.comment && <p className="text-gray-500 italic mt-0.5">→ {item.comment}</p>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const ScoreSummaryTable = ({ responses }) => {
  const rows = DIAGNOSTIC_AXES.map(axis => {
    const axisData = responses[axis.key] || {};
    const answered = axis.questions.filter(q => axisData[q.id]?.score > 0);
    const avg = answered.length > 0 ? answered.reduce((s, q) => s + (axisData[q.id]?.score || 0), 0) / answered.length : 0;
    const counts = { force: 0, faiblesse: 0, opportunite: 0, menace: 0 };
    answered.forEach(q => {
      const cat = axisData[q.id]?.category || autoCategory(axisData[q.id]?.score);
      if (cat && counts[cat] !== undefined) counts[cat]++;
    });
    return { axis, avg, answered: answered.length, total: axis.questions.length, counts };
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left px-4 py-2 font-medium text-gray-700">Axe</th>
            <th className="text-center px-3 py-2 font-medium text-gray-700">Rép.</th>
            <th className="text-center px-3 py-2 font-medium text-gray-700">Score</th>
            <th className="text-center px-3 py-2 font-medium text-green-700">Forces</th>
            <th className="text-center px-3 py-2 font-medium text-red-700">Faiblesses</th>
            <th className="text-center px-3 py-2 font-medium text-blue-700">Opp.</th>
            <th className="text-center px-3 py-2 font-medium text-orange-700">Menaces</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ axis, avg, answered, total, counts }) => (
            <tr key={axis.key} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">{axis.label}</td>
              <td className="px-3 py-2 text-center text-gray-500">{answered}/{total}</td>
              <td className="px-3 py-2 text-center">
                {avg > 0
                  ? <span className={`font-bold px-2 py-0.5 rounded text-xs ${scoreBg(Math.round(avg))}`}>{avg.toFixed(1)}</span>
                  : <span className="text-gray-300">—</span>}
              </td>
              <td className="px-3 py-2 text-center">{counts.force > 0 ? <span className="font-medium text-green-700">{counts.force}</span> : <span className="text-gray-300">—</span>}</td>
              <td className="px-3 py-2 text-center">{counts.faiblesse > 0 ? <span className="font-medium text-red-700">{counts.faiblesse}</span> : <span className="text-gray-300">—</span>}</td>
              <td className="px-3 py-2 text-center">{counts.opportunite > 0 ? <span className="font-medium text-blue-700">{counts.opportunite}</span> : <span className="text-gray-300">—</span>}</td>
              <td className="px-3 py-2 text-center">{counts.menace > 0 ? <span className="font-medium text-orange-700">{counts.menace}</span> : <span className="text-gray-300">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const Diagnostic = () => {
  const [searchParams] = useSearchParams();
  const entrepreneurIdParam = searchParams.get('entrepreneur');
  const { entrepreneurs, loading: entLoading, fetchEntrepreneurs } = useCoaching();

  const [selectedEntrepreneurId, setSelectedEntrepreneurId] = useState(entrepreneurIdParam || '');
  const [diagnosticId, setDiagnosticId] = useState(null);
  const [responses, setResponses] = useState({});
  const [openAxes, setOpenAxes] = useState({});
  const [activeTab, setActiveTab] = useState('diagnostic');
  const [diagnosticDate, setDiagnosticDate] = useState(new Date().toISOString().split('T')[0]);
  const [observations, setObservations] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDiag, setLoadingDiag] = useState(false);

  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    fetchEntrepreneurs();
  }, [fetchEntrepreneurs]);

  const loadDiagnostic = useCallback(async (entrepreneurId) => {
    setLoadingDiag(true);
    try {
      const { data } = await axios.get(`/api/coaching/diagnostics/entrepreneur/${entrepreneurId}`);
      const diag = data.diagnostic;
      if (diag) {
        setDiagnosticId(diag.id);
        setResponses(diag.responses || {});
        setDiagnosticDate(diag.diagnostic_date || new Date().toISOString().split('T')[0]);
        setObservations(diag.observations || '');
      } else {
        setDiagnosticId(null);
        setResponses({});
        setDiagnosticDate(new Date().toISOString().split('T')[0]);
        setObservations('');
      }
    } catch (err) {
      toast.error('Erreur chargement diagnostic');
    } finally {
      setLoadingDiag(false);
      setDirty(false);
    }
    const opened = {};
    DIAGNOSTIC_AXES.forEach((a, i) => { opened[a.key] = i === 0; });
    setOpenAxes(opened);
  }, []);

  useEffect(() => {
    if (selectedEntrepreneurId) loadDiagnostic(selectedEntrepreneurId);
  }, [selectedEntrepreneurId, loadDiagnostic]);

  const setResponse = (axisKey, questionId, field, value) => {
    setResponses(prev => ({
      ...prev,
      [axisKey]: {
        ...prev[axisKey],
        [questionId]: { ...(prev[axisKey]?.[questionId] || {}), [field]: value }
      }
    }));
    setDirty(true);
  };

  const doSave = useCallback(async (currentResponses, currentDate, currentObs, currentId) => {
    if (!selectedEntrepreneurId) return;
    setSaving(true);
    try {
      const payload = {
        entrepreneur_id: selectedEntrepreneurId,
        diagnostic_date: currentDate,
        responses: currentResponses,
        observations: currentObs,
      };
      const { data } = await axios.post('/api/coaching/diagnostics', payload);
      setDiagnosticId(data.diagnostic.id);
      setDirty(false);
    } catch (err) {
      toast.error('Erreur sauvegarde diagnostic');
    } finally {
      setSaving(false);
    }
  }, [selectedEntrepreneurId]);

  // Auto-save 3 s after last change
  useEffect(() => {
    if (!dirty || !selectedEntrepreneurId) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      doSave(responses, diagnosticDate, observations, diagnosticId);
    }, 3000);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [dirty, responses, diagnosticDate, observations, diagnosticId, selectedEntrepreneurId, doSave]);

  const handleSaveNow = () => {
    clearTimeout(saveTimeoutRef.current);
    doSave(responses, diagnosticDate, observations, diagnosticId);
    toast.success('Diagnostic enregistré');
  };

  const handleDelete = async () => {
    if (!diagnosticId) return;
    if (!window.confirm('Supprimer définitivement ce diagnostic ?')) return;
    try {
      await axios.delete(`/api/coaching/diagnostics/${diagnosticId}`);
      setDiagnosticId(null);
      setResponses({});
      setObservations('');
      setDirty(false);
      toast.success('Diagnostic supprimé');
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const handleExportCSV = () => {
    const ent = entrepreneurs.find(e => e.id === selectedEntrepreneurId);
    const rows = [['Axe', 'Question', 'Score', 'Catégorie', 'Commentaire']];
    DIAGNOSTIC_AXES.forEach(axis => {
      const axisData = responses[axis.key] || {};
      axis.questions.forEach(q => {
        const entry = axisData[q.id] || {};
        const cat = entry.category || autoCategory(entry.score) || '';
        rows.push([axis.label, q.text, entry.score || '', SWOT_CATEGORIES[cat]?.label || '', entry.comment || '']);
      });
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnostic-${(ent?.company_name || 'entreprise').replace(/\s+/g, '-')}-${diagnosticDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const completionStats = () => {
    let total = 0, answered = 0;
    DIAGNOSTIC_AXES.forEach(axis => {
      total += axis.questions.length;
      const axisData = responses[axis.key] || {};
      axis.questions.forEach(q => { if (axisData[q.id]?.score > 0) answered++; });
    });
    return { total, answered, pct: total > 0 ? Math.round((answered / total) * 100) : 0 };
  };

  const axisProgress = (axisKey) => {
    const axis = DIAGNOSTIC_AXES.find(a => a.key === axisKey);
    if (!axis) return 0;
    const axisData = responses[axisKey] || {};
    return axis.questions.filter(q => axisData[q.id]?.score > 0).length;
  };

  const axisAvg = (axisKey) => {
    const axis = DIAGNOSTIC_AXES.find(a => a.key === axisKey);
    if (!axis) return 0;
    const axisData = responses[axisKey] || {};
    const scored = axis.questions.map(q => axisData[q.id]?.score || 0).filter(s => s > 0);
    return scored.length > 0 ? scored.reduce((a, b) => a + b, 0) / scored.length : 0;
  };

  const { total, answered, pct } = completionStats();
  const selectedEntrepreneur = entrepreneurs.find(e => e.id === selectedEntrepreneurId);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Diagnostic d'entreprise</h1>
          <p className="text-sm text-gray-500 mt-0.5">Analyse complète sur 9 axes · Forces, Faiblesses, Opportunités, Menaces</p>
        </div>
        {selectedEntrepreneurId && (
          <div className="flex items-center space-x-2">
            {saving && <span className="text-xs text-gray-400 animate-pulse">Enregistrement…</span>}
            {dirty && !saving && (
              <span className="text-xs text-amber-600">● Non sauvegardé</span>
            )}
            {!dirty && diagnosticId && (
              <span className="text-xs text-green-600">✓ Sauvegardé</span>
            )}
            <button
              onClick={handleSaveNow}
              disabled={saving}
              className="flex items-center space-x-1.5 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
            >
              <IconSave /><span>Sauvegarder</span>
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-1.5 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
            >
              <IconDownload /><span>CSV</span>
            </button>
          </div>
        )}
      </div>

      {/* Sélection entrepreneur */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entrepreneur / Entreprise diagnostiquée</label>
            <select
              value={selectedEntrepreneurId}
              onChange={e => setSelectedEntrepreneurId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              <option value="">-- Sélectionner un entrepreneur --</option>
              {entrepreneurs.map(e => (
                <option key={e.id} value={e.id}>{e.company_name} — {e.entrepreneur_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date du diagnostic</label>
            <input
              type="date"
              value={diagnosticDate}
              onChange={e => { setDiagnosticDate(e.target.value); setDirty(true); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
        </div>

        {selectedEntrepreneur && (
          <div className="pt-2 border-t border-gray-100 flex flex-wrap gap-4 text-sm items-center">
            <div><span className="text-gray-500">Entreprise : </span><span className="font-medium">{selectedEntrepreneur.company_name}</span></div>
            <div><span className="text-gray-500">Dirigeant : </span><span className="font-medium">{selectedEntrepreneur.entrepreneur_name}</span></div>
            {selectedEntrepreneur.sector && <div><span className="text-gray-500">Secteur : </span><span className="font-medium">{selectedEntrepreneur.sector}</span></div>}
            <div className="ml-auto flex items-center space-x-3">
              <div className="text-xs text-gray-500">{answered}/{total} questions</div>
              <div className="w-28 bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className={`text-xs font-bold ${pct === 100 ? 'text-green-600' : pct > 50 ? 'text-yellow-600' : 'text-gray-500'}`}>{pct}%</div>
              {diagnosticId && (
                <button onClick={handleDelete} className="text-xs text-red-500 hover:text-red-700 flex items-center space-x-1">
                  <IconTrash /><span>Réinitialiser</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {!selectedEntrepreneurId && !entLoading && (
        <div className="bg-white rounded-lg border border-dashed border-gray-300 p-12 text-center text-gray-400">
          Sélectionnez un entrepreneur pour démarrer ou reprendre son diagnostic
        </div>
      )}

      {selectedEntrepreneurId && loadingDiag && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {selectedEntrepreneurId && !loadingDiag && (
        <>
          {/* Onglets */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
            {[
              { key: 'diagnostic', label: 'Questionnaire' },
              { key: 'resultats',  label: 'Résultats & SWOT' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Questionnaire ── */}
          {activeTab === 'diagnostic' && (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800 flex flex-wrap gap-x-6 gap-y-1">
                <span className="font-semibold">Guide de notation :</span>
                <span><strong className="text-red-600">1</strong> = Très insuffisant</span>
                <span><strong className="text-red-500">2</strong> = Insuffisant</span>
                <span><strong className="text-yellow-600">3</strong> = Moyen / À améliorer</span>
                <span><strong className="text-green-500">4</strong> = Satisfaisant</span>
                <span><strong className="text-green-600">5</strong> = Excellent</span>
                <span className="ml-auto text-blue-600">Sauvegarde automatique après 3 s</span>
              </div>

              {DIAGNOSTIC_AXES.map(axis => {
                const isOpen = openAxes[axis.key];
                const prog = axisProgress(axis.key);
                const avg = axisAvg(axis.key);
                const axisData = responses[axis.key] || {};

                return (
                  <div key={axis.key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setOpenAxes(p => ({ ...p, [axis.key]: !p[axis.key] }))}
                      className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium border ${axis.colorClass}`}>
                          {axis.label}
                        </span>
                        <span className="text-sm text-gray-500 hidden sm:block truncate">{axis.description}</span>
                      </div>
                      <div className="flex items-center space-x-3 ml-3 shrink-0">
                        <span className="text-xs text-gray-400">{prog}/{axis.questions.length}</span>
                        {avg > 0 && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${scoreBg(Math.round(avg))}`}>
                            {avg.toFixed(1)}
                          </span>
                        )}
                        <IconChevron open={isOpen} />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t border-gray-100 divide-y divide-gray-50">
                        {axis.questions.map((q, qi) => {
                          const entry = axisData[q.id] || {};
                          const suggestedCat = autoCategory(entry.score);
                          return (
                            <div key={q.id} className="px-5 py-4">
                              <div className="flex items-start space-x-3">
                                <span className="shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-medium flex items-center justify-center mt-0.5">
                                  {qi + 1}
                                </span>
                                <div className="flex-1 space-y-3">
                                  <p className="text-sm text-gray-800 font-medium leading-snug">{q.text}</p>
                                  <div className="flex flex-wrap items-center gap-4">
                                    <div>
                                      <p className="text-xs text-gray-500 mb-1.5">Note</p>
                                      <StarRating
                                        value={entry.score || 0}
                                        onChange={v => setResponse(axis.key, q.id, 'score', v)}
                                      />
                                    </div>
                                    {entry.score > 0 && (
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1.5">Classification SWOT</p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {Object.entries(SWOT_CATEGORIES).map(([k, v]) => {
                                            const isActive = entry.category === k || (!entry.category && k === suggestedCat);
                                            return (
                                              <button
                                                key={k}
                                                type="button"
                                                onClick={() => setResponse(axis.key, q.id, 'category', entry.category === k ? null : k)}
                                                className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${
                                                  isActive
                                                    ? `${v.bg} ${v.text} ${v.border} ring-1 ring-current`
                                                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                                                }`}
                                              >
                                                {v.icon} {v.label}
                                                {k === suggestedCat && !entry.category && <span className="ml-1 opacity-60">(auto)</span>}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {entry.score > 0 && (
                                    <textarea
                                      rows={2}
                                      value={entry.comment || ''}
                                      onChange={e => setResponse(axis.key, q.id, 'comment', e.target.value)}
                                      placeholder="Observations, éléments de contexte, recommandations… (facultatif)"
                                      className="w-full border border-gray-200 rounded px-3 py-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Observations générales */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Observations générales du conseiller</label>
                <textarea
                  rows={4}
                  value={observations}
                  onChange={e => { setObservations(e.target.value); setDirty(true); }}
                  placeholder="Synthèse globale, contexte particulier, priorités identifiées, recommandations stratégiques…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveNow}
                  disabled={saving}
                  className="flex items-center space-x-2 px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                >
                  <IconSave /><span>Enregistrer le diagnostic</span>
                </button>
              </div>
            </div>
          )}

          {/* ── Résultats ── */}
          {activeTab === 'resultats' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-900 mb-4">Graphique radar — Scores par axe</h2>
                  <RadarChart responses={responses} />
                  <p className="text-xs text-gray-400 text-center mt-2">Scores moyens de 0 à 5 par axe d'analyse</p>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-900 mb-4">Synthèse par axe</h2>
                  <ScoreSummaryTable responses={responses} />
                  <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center space-x-2"><div className="w-3 h-3 rounded-sm bg-green-100 border border-green-300" /><span className="text-gray-600">Score 4–5 : Force</span></div>
                    <div className="flex items-center space-x-2"><div className="w-3 h-3 rounded-sm bg-yellow-100 border border-yellow-300" /><span className="text-gray-600">Score 3 : Moyen</span></div>
                    <div className="flex items-center space-x-2"><div className="w-3 h-3 rounded-sm bg-red-100 border border-red-300" /><span className="text-gray-600">Score 1–2 : Faiblesse</span></div>
                    <div className="flex items-center space-x-2"><div className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-300" /><span className="text-gray-600">Bleu/Orange : Opp./Menace</span></div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Matrice SWOT</h2>
                  <p className="text-xs text-gray-400">Classification automatique (score) ou manuelle</p>
                </div>
                <SwotMatrix responses={responses} />
              </div>

              {observations && (
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-900 mb-2">Observations du conseiller</h2>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{observations}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleExportCSV}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                >
                  <IconDownload /><span>Exporter en CSV</span>
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>Imprimer</span>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Diagnostic;
